import { apiClient } from '@/services/api/client'

export interface VoicePerson {
  person_id: string
  name: string
}
export interface VoiceSide {
  voiceprint_id: string
  document_id: string
  voice: number | null
  origin: 'sample' | 'recording'
  filename: string | null
  quality: string | null
  speech_seconds: number | null
  person: VoicePerson | null
  audio_url: string | null
}
export interface VoiceTrack extends VoiceSide {
  state: 'ready' | 'no_speech'
  quality_flags: string[] | null
}
export interface TranscriptTurn {
  index: number
  voice: number
  start_ms: number
  end_ms: number
  text: string
  language: string | null
}
export interface RecordingTranscript {
  document_id: string
  diarization: string
  turns: TranscriptTurn[]
  tracks: VoiceTrack[]
  member_role: string
}
export interface RecordingPair {
  pair_id: string
  status: 'suggested' | 'confirmed' | 'rejected'
  similarity: number
  band: string
  provisional: boolean
  left: VoiceSide
  right: VoiceSide
  /** When the pair was decided; none while it waits. */
  decided_at?: string | null
}
export interface RecordingPairs {
  pairs: RecordingPair[]
  member_role: string
}
export interface VoiceSearch {
  job_id: string
  status: string
  error: string | null
  result: {
    status: 'ready' | 'no_speech' | 'low_quality' | 'disabled'
    matches_created: number
    indexed_documents: number
    skipped_documents: { document_id: string; reason: string }[]
  } | null
}
export interface RecordingScope {
  caseId: string
  documentId: string
  filename: string
}

const path = (caseId: string) => `/v1/cases/${encodeURIComponent(caseId)}`
export const recordingVoiceKeys = {
  transcript: (scope: RecordingScope) => ['recording-voices', scope.caseId, scope.documentId],
  pairs: (caseId: string) => ['recording-voice-pairs', caseId],
}
export const recordingVoicesApi = {
  audio: (url: string, signal?: AbortSignal) =>
    apiClient.get<Blob>(url.replace(/^\/api(?=\/)/, ''), { responseType: 'blob', signal }),
  transcript: (scope: RecordingScope, signal?: AbortSignal) =>
    apiClient.get<RecordingTranscript>(
      `${path(scope.caseId)}/documents/${scope.documentId}/voices`,
      { signal }
    ),
  search: (scope: RecordingScope, voice: number) =>
    apiClient.post<VoiceSearch>(
      `${path(scope.caseId)}/documents/${scope.documentId}/voices/search`,
      { voice }
    ),
  status: (caseId: string, jobId: string, signal?: AbortSignal) =>
    apiClient.get<VoiceSearch>(`${path(caseId)}/recording-voices/searches/${jobId}`, { signal }),
  pairs: (caseId: string, signal?: AbortSignal) =>
    apiClient.get<RecordingPairs>(`${path(caseId)}/recording-voices/pairs`, { signal }),
  reopen: (caseId: string, pairId: string) =>
    apiClient.post<RecordingPair>(`${path(caseId)}/recording-voices/pairs/${pairId}/reopen`),
  decide: (caseId: string, pairId: string, decision: 'same' | 'different') =>
    apiClient.post<RecordingPair>(`${path(caseId)}/recording-voices/pairs/${pairId}/decision`, {
      decision,
    }),
}
